import type { InvoicesQueryParams, InvoicesResponse } from './types';

import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoices } from './fetchInvoices';

const EMPTY_INVOICES_RESPONSE: InvoicesResponse = {
  data: [],
  hasNextPage: false,
  paginatedCount: 0,
  totalCount: 0,
};

/**
 * Generates the query options for the invoices query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching invoices.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getInvoicesQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams: Partial<InvoicesQueryParams>;
  options?: Omit<UseQueryOptions<InvoicesResponse>, 'queryKey' | 'queryFn'>;
}) => {
  return {
    enabled: Boolean(queryParams.direction),
    queryFn: async (options: QueryFunctionContext) =>
      (await fetchInvoices(queryParams, options)) ?? EMPTY_INVOICES_RESPONSE,
    queryKey: queryKeysInvoice.getAll(queryParams),
    staleTime: ms('60s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for all (incoming or outgoing) invoices with given query parameters.
 * Handles pagination and prefetches the next page of invoices for better performance.
 *
 * @param queryParams - The query parameters for fetching invoices.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoices_for_user_invoice_get
 */
export const useQueryInvoices = (
  queryParams: Partial<InvoicesQueryParams>,
  options?: Omit<UseQueryOptions<InvoicesResponse>, 'queryKey' | 'queryFn'> & {
    prefetchNextPage?: boolean;
  },
) => {
  const queryClient = useQueryClient();
  const { prefetchNextPage = false, ...queryOptions } = options ?? {};
  const limit = queryParams.limit ?? 100;
  const offset = queryParams.offset ?? 0;
  const totalCount = queryClient.getQueryData<InvoicesResponse>(
    queryKeysInvoice.getAll(queryParams),
  )?.totalCount;

  // Prefetch the next page for better UX
  useEffect(() => {
    if (!prefetchNextPage) return;
    if (!queryParams.direction) return;
    if (totalCount != null && offset + limit >= totalCount) {
      return;
    }

    const nextPageQueryParams = {
      ...queryParams,
      offset: offset + limit,
    };

    void queryClient.prefetchQuery<InvoicesResponse>({
      queryFn: async (options) =>
        (await fetchInvoices(nextPageQueryParams, options)) ??
        EMPTY_INVOICES_RESPONSE,
      queryKey: queryKeysInvoice.getAll(nextPageQueryParams),
      staleTime: ms('60s'),
    });
  }, [limit, offset, prefetchNextPage, queryParams, queryClient, totalCount]);

  return useQuery<InvoicesResponse>({
    ...getInvoicesQueryOptions({ options: queryOptions, queryParams }),
    placeholderData: keepPreviousData,
  });
};
